export const SCROLL_INTO_VIEW_DURATION_MS = 300;

/** アコーディオンを開いたときに、上のエリアを覗かせるための余白 */
export const SCROLL_TO_TOP_OFFSET_PX = 50;

const SCROLLABLE_OVERFLOW_Y = new Set(['auto', 'scroll', 'overlay']);

function findVerticalScrollContainer(element: HTMLElement): HTMLElement {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (SCROLLABLE_OVERFLOW_Y.has(overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return document.documentElement;
}

function clampScrollTop(scrollContainer: HTMLElement, scrollTop: number): number {
  const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
  return Math.max(0, Math.min(maxScrollTop, scrollTop));
}

function computeCenteredScrollTop(element: HTMLElement, scrollContainer: HTMLElement): number {
  const elementRect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementCenterY = elementRect.top + elementRect.height / 2;
  const containerCenterY = containerRect.top + containerRect.height / 2;

  return clampScrollTop(
    scrollContainer,
    scrollContainer.scrollTop + (elementCenterY - containerCenterY),
  );
}

function computeTopAlignedScrollTop(
  element: HTMLElement,
  scrollContainer: HTMLElement,
  offsetPx: number,
): number {
  const elementRect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();

  return clampScrollTop(
    scrollContainer,
    scrollContainer.scrollTop + (elementRect.top - containerRect.top) - offsetPx,
  );
}

function animateScrollTop(
  scrollContainer: HTMLElement,
  targetScrollTop: number,
  durationMs: number,
): Promise<void> {
  const container = scrollContainer;
  const startScrollTop = container.scrollTop;

  if (durationMs <= 0 || startScrollTop === targetScrollTop) {
    container.scrollTop = targetScrollTop;
    return Promise.resolve();
  }

  const distance = targetScrollTop - startScrollTop;
  const startTime = performance.now();

  return new Promise<void>((resolve) => {
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      container.scrollTop = startScrollTop + distance * progress;

      if (progress < 1) {
        requestAnimationFrame(step);
        return;
      }

      resolve();
    };

    requestAnimationFrame(step);
  });
}

export function scrollIntoView(element: HTMLElement, durationMs: number): Promise<void> {
  const scrollContainer = findVerticalScrollContainer(element);

  return animateScrollTop(
    scrollContainer,
    computeCenteredScrollTop(element, scrollContainer),
    durationMs,
  );
}

/**
 * 要素の開始位置をスクロールコンテナの上端に寄せる。
 * offsetPx の分だけ下げて、上に続きがあることを見せる。
 */
export function scrollToElementTop(
  element: HTMLElement,
  durationMs: number,
  offsetPx: number = SCROLL_TO_TOP_OFFSET_PX,
): Promise<void> {
  const scrollContainer = findVerticalScrollContainer(element);

  return animateScrollTop(
    scrollContainer,
    computeTopAlignedScrollTop(element, scrollContainer, offsetPx),
    durationMs,
  );
}
